import { useEffect, useState, useRef, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Users, TrendingUp, AlertTriangle, DoorOpen, ShieldOff,
  UserPlus, Banknote, CreditCard, Check, X, ChevronRight,
  RefreshCw, Zap, CalendarClock, Clock, Activity,
} from 'lucide-react'
import { useSSE } from '@/hooks/useSSE'
import { useProxyConfig } from '@/hooks/useProxyConfig'
import { getEvents, getDailyEventCounts, type AccessEvent } from '@/api/events'
import { getUsers } from '@/api/users'
import { getDevices, type Device } from '@/api/devices'
import { getExpiringToday, listMemberships, type Membership } from '@/api/memberships'
import { membershipMessage } from '@/lib/membership-status'
import { MemberSearch } from '@/components/shared/MemberSearch'
import { MembershipForm } from './memberships/MembershipForm'
import { DoorPanel } from '@/components/shared/DoorPanel'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import type { User } from '@/api/users'

interface Metrics {
  todayAccesses: number
  activeMembers: number
  expiringToday: number
  expiringNames: string[]
  noAccess: number
}

interface DayCount { date: string; count: number; label: string }

function hasNoAccess(u: { face_uploaded: boolean; card_no?: string | null; pin?: string | null }) {
  return !u.face_uploaded && !u.card_no && !u.pin
}

function timeAgo(dateStr: string): string {
  const diff = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000)
  if (diff < 60) return 'ahora'
  if (diff < 3600) return `${Math.floor(diff / 60)} min`
  return `${Math.floor(diff / 3600)} h`
}

function initials(name: string): string {
  return name.split(' ').slice(0, 2).map(w => w[0]).join('').toUpperCase()
}

// ── Gráfica de área (semanal) ─────────────────────────────────────────────────
function AreaChart({ days }: { days: DayCount[] }) {
  const W = 300, H = 56
  const maxVal = Math.max(...days.map(d => d.count), 1)
  const pts = days.map((d, i) => ({
    x: (i / (days.length - 1)) * W,
    y: H - 4 - (d.count / maxVal) * (H - 12),
    v: d.count,
  }))
  const smooth = (arr: typeof pts) => {
    return arr.map((p, i) => {
      if (i === 0) return `M${p.x.toFixed(1)},${p.y.toFixed(1)}`
      const prev = arr[i - 1]
      const cx = ((prev.x + p.x) / 2).toFixed(1)
      return `C${cx},${prev.y.toFixed(1)} ${cx},${p.y.toFixed(1)} ${p.x.toFixed(1)},${p.y.toFixed(1)}`
    }).join(' ')
  }
  const linePath = smooth(pts)
  const areaPath = `${linePath} L${pts[pts.length - 1].x},${H} L${pts[0].x},${H}Z`

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-14" preserveAspectRatio="none">
        <defs>
          <linearGradient id="ag" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="currentColor" stopOpacity="0.20" />
            <stop offset="100%" stopColor="currentColor" stopOpacity="0.01" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#ag)" className="text-accent" />
        <path d={linePath} fill="none" stroke="currentColor" strokeWidth="1.8"
          strokeLinecap="round" strokeLinejoin="round" className="text-accent" />
        {pts.map((p, i) => (
          <circle key={i} cx={p.x} cy={p.y} r={p.v > 0 ? 2.5 : 1.5}
            fill={p.v > 0 ? 'currentColor' : '#334155'} className={p.v > 0 ? 'text-accent' : ''} />
        ))}
      </svg>
      <div className="flex justify-between mt-2">
        {days.map((d, i) => (
          <div key={i} className="flex flex-col items-center gap-0.5">
            <span className={`text-[9px] font-bold ${d.count > 0 ? 'text-text-muted' : 'text-border'}`}>
              {d.label}
            </span>
            <span className={`text-[9px] tabular-nums font-semibold ${d.count > 0 ? 'text-accent' : 'text-transparent'}`}>
              {d.count || '.'}
            </span>
          </div>
        ))}
      </div>
    </div>
  )
}

// ── Gráfica de barras (horaria) ───────────────────────────────────────────────
function HourBars({ hourly }: { hourly: number[] }) {
  const slice = hourly.slice(6, 23)
  const maxVal = Math.max(...slice, 1)
  const now = new Date().getHours()

  return (
    <div>
      <div className="flex items-end gap-[3px] h-12">
        {slice.map((v, i) => {
          const h = i + 6
          const pct = v > 0 ? Math.max(10, (v / maxVal) * 100) : 5
          const isNow = h === now
          return (
            <div key={i} className="flex-1 flex flex-col justify-end" title={`${h}:00 — ${v} accesos`}>
              <div
                className={`rounded-t-[3px] transition-all ${isNow ? 'bg-accent opacity-100' : v > 0 ? 'bg-accent/50' : 'bg-border/40'}`}
                style={{ height: `${pct}%` }}
              />
            </div>
          )
        })}
      </div>
      <div className="flex justify-between mt-2 px-0.5">
        {['6am','9am','12pm','3pm','6pm','10pm'].map((l, i) => (
          <span key={i} className="text-[9px] text-text-muted">{l}</span>
        ))}
      </div>
    </div>
  )
}

// ── Dashboard ─────────────────────────────────────────────────────────────────
export function Dashboard() {
  const navigate = useNavigate()
  const { proxyUrl, proxyToken, isElectron } = useProxyConfig()
  const [devices, setDevices]   = useState<Device[]>([])
  const { events: sseEvents }   = useSSE(proxyUrl, proxyToken, devices.map(d => d.id))
  const [metrics, setMetrics]   = useState<Metrics>({ todayAccesses: 0, activeMembers: 0, expiringToday: 0, expiringNames: [], noAccess: 0 })
  const [feedEvents, setFeedEvents]   = useState<AccessEvent[]>([])
  const [weekDays, setWeekDays]       = useState<DayCount[]>([])
  const [membershipByUser, setMembershipByUser] = useState<Map<string, Membership>>(new Map())
  const [userByEmployeeNo, setUserByEmployeeNo] = useState<Map<string, User>>(new Map())
  const [selectedUser, setSelectedUser] = useState<User | null>(null)
  const [membershipOpen, setMembershipOpen] = useState(false)
  const [doorOpen, setDoorOpen] = useState(false)
  const [now, setNow] = useState(new Date())
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(t)
  }, [])

  useEffect(() => {
    const today = new Date().toISOString().split('T')[0]
    const d7 = new Date(); d7.setDate(d7.getDate() - 6)
    const weekStart = d7.toISOString().split('T')[0]

    Promise.allSettled([
      getEvents({ startDate: today, limit: 300 }),
      getDailyEventCounts(weekStart, today),
      getUsers(),
      getDevices(),
      getExpiringToday(),
      listMemberships(undefined, 500),
    ]).then(([todayEv, weekCounts, usersRes, devRes, membRes, allMembRes]) => {
      const todayEvents = todayEv.status === 'fulfilled' ? todayEv.value.events : []
      const todayTotal = todayEv.status === 'fulfilled' ? todayEv.value.total : todayEvents.length
      const users = usersRes.status === 'fulfilled' ? usersRes.value.users : []
      const expiring: Membership[] = membRes.status === 'fulfilled' ? membRes.value.memberships : []
      const userMap = new Map(users.map(u => [u.id, u.name]))
      if (devRes.status === 'fulfilled') setDevices(devRes.value.devices)
      if (allMembRes.status === 'fulfilled') {
        setMembershipByUser(new Map(allMembRes.value.memberships.map(m => [m.user_id, m])))
      }
      // Live SSE-pushed feed rows only carry employeeNo (device-stream-manager's
      // AccessEvent payload has no user_id) — resolve through this map instead
      // of ev.user_id, or "Ver perfil" and the membership line silently never
      // show for anything that arrived in real time (most of the feed).
      setUserByEmployeeNo(new Map(users.filter(u => u.employee_no).map(u => [u.employee_no!, u])))
      setFeedEvents(todayEvents.slice(0, 20))
      setMetrics({ todayAccesses: todayTotal, activeMembers: users.length, expiringToday: expiring.length, expiringNames: expiring.map(m => userMap.get(m.user_id) ?? '').filter(Boolean), noAccess: users.filter(hasNoAccess).length })

      if (weekCounts.status === 'fulfilled') {
        const counts = new Map(weekCounts.value.days.map(d => [d.date, d.count]))
        const DAY_LABELS = ['D', 'L', 'M', 'X', 'J', 'V', 'S']
        setWeekDays(Array.from({ length: 7 }, (_, i) => {
          const d = new Date(); d.setDate(d.getDate() - (6 - i))
          const dateStr = d.toISOString().split('T')[0]
          return { date: dateStr, count: counts.get(dateStr) ?? 0, label: DAY_LABELS[d.getDay()] }
        }))
      }
    })
  }, [])

  // sseEvents is newest-first (see useSSE) — [0] is the one that just arrived,
  // not [length-1]. The field names below match the real accessEvent payload
  // from device-stream-manager (employeeNo/name/eventType/occurredAt) — the
  // SseEvent type's userName/type/timestamp fields don't exist on the wire.
  const lastProcessedSseCount = useRef(0)
  useEffect(() => {
    if (sseEvents.length === 0 || sseEvents.length === lastProcessedSseCount.current) return
    lastProcessedSseCount.current = sseEvents.length
    const last = sseEvents[0]
    if (last.type !== 'accessEvent') return
    setFeedEvents(prev => [{
      id: `${last.employeeNo ?? 'unknown'}-${last.occurredAt}`,
      employee_no: last.employeeNo ?? undefined,
      user_name: last.name ?? undefined,
      door_no: last.doorNo ?? undefined,
      event_type: last.eventType,
      occurred_at: last.occurredAt,
    }, ...prev].slice(0, 20))
    setMetrics(prev => ({ ...prev, todayAccesses: prev.todayAccesses + 1 }))
  }, [sseEvents])

  useEffect(() => {
    if (isElectron) return
    const today = new Date().toISOString().split('T')[0]
    pollRef.current = setInterval(async () => {
      try { setFeedEvents((await getEvents({ startDate: today, limit: 20 })).events) } catch { /* silent */ }
    }, 30000)
    return () => { if (pollRef.current) clearInterval(pollRef.current) }
  }, [isElectron])

  const hourlyData = useMemo(() => {
    const buckets = Array(24).fill(0)
    feedEvents.forEach(ev => { buckets[new Date(ev.occurred_at).getHours()]++ })
    return buckets
  }, [feedEvents])

  // Unmatched attempts (card/face not recognized, device noise) show as
  // "Desconocido" with no name — not useful in an activity summary meant to
  // answer "who came in today", so the feed only lists identified members.
  const namedFeedEvents = useMemo(
    () => feedEvents.filter(ev => !!ev.user_name),
    [feedEvents],
  )

  const hour = now.getHours()
  const greeting = hour < 12 ? 'Buenos días' : hour < 18 ? 'Buenas tardes' : 'Buenas noches'
  const dateLabel = now.toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })
  const timeLabel = now.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', second: '2-digit' })

  const peakHour = hourlyData.indexOf(Math.max(...hourlyData))

  return (
    <div className="flex flex-col gap-5">

      {/* ── Alerta vencimientos (top para que se vea primero) ── */}
      {metrics.expiringToday > 0 && (
        <div className="flex items-center gap-4 bg-red-500/8 border border-red-500/25 rounded-2xl px-5 py-3.5">
          <div className="w-9 h-9 rounded-xl bg-red-500/15 border border-red-500/20 flex items-center justify-center shrink-0">
            <CalendarClock className="w-4 h-4 text-red-500" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-text-primary text-sm font-bold">
              {metrics.expiringToday} membresía{metrics.expiringToday > 1 ? 's' : ''} vence{metrics.expiringToday > 1 ? 'n' : ''} hoy
            </p>
            <p className="text-text-muted text-xs truncate mt-0.5">{metrics.expiringNames.join(' · ')}</p>
          </div>
          <button
            onClick={() => navigate('/memberships')}
            className="shrink-0 flex items-center gap-1.5 bg-red-500 hover:bg-red-600 text-white text-xs font-bold px-4 py-2 rounded-xl transition-colors cursor-pointer"
          >
            Gestionar <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ── Cabecera ── */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-text-primary font-bold text-xl leading-tight">{greeting}</h1>
          <p className="text-text-muted text-xs mt-0.5 capitalize">{dateLabel}</p>
        </div>
        <div className="flex items-center gap-2.5">
          <div className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-[10px] font-bold ${isElectron ? 'bg-emerald-500/8 border-emerald-500/25 text-emerald-600 dark:text-emerald-400' : 'bg-surface border-border text-text-muted'}`}>
            {isElectron
              ? <><span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />En vivo</>
              : <><RefreshCw className="w-3 h-3" />Cada 30 s</>}
          </div>
          <div className="bg-surface border border-border rounded-xl px-3 py-1.5">
            <p className="text-text-primary font-mono font-bold text-base tabular-nums">{timeLabel}</p>
          </div>
        </div>
      </div>

      {/* ── 4 Métricas ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <MetricCard
          icon={<TrendingUp className="w-4 h-4" />}
          label="Accesos hoy"
          value={metrics.todayAccesses}
          detail={isElectron ? 'tiempo real' : 'actualiza cada 30 s'}
          color="emerald"
        />
        <MetricCard
          icon={<Users className="w-4 h-4" />}
          label="Miembros activos"
          value={metrics.activeMembers}
          detail="en el sistema"
          color="blue"
          action={{ label: 'Ver miembros', onClick: () => navigate('/members') }}
        />
        <MetricCard
          icon={<AlertTriangle className="w-4 h-4" />}
          label="Vencen hoy"
          value={metrics.expiringToday}
          detail={metrics.expiringToday > 0 ? 'requieren renovación' : 'sin vencimientos'}
          color={metrics.expiringToday > 0 ? 'red' : 'muted'}
          action={metrics.expiringToday > 0 ? { label: 'Renovar', onClick: () => navigate('/memberships') } : undefined}
        />
        <MetricCard
          icon={<ShieldOff className="w-4 h-4" />}
          label="Sin acceso"
          value={metrics.noAccess}
          detail={metrics.noAccess > 0 ? 'sin credencial' : 'todos configurados'}
          color={metrics.noAccess > 0 ? 'amber' : 'muted'}
          action={metrics.noAccess > 0 ? { label: 'Configurar', onClick: () => navigate('/members?filter=no-access') } : undefined}
        />
      </div>

      {/* ── Cuerpo principal ── */}
      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4">

        {/* ── Columna izquierda ── */}
        <div className="flex flex-col gap-3">

          {/* Venta Rápida */}
          <div className="bg-surface border border-border rounded-2xl overflow-hidden">
            <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-accent/10 border border-accent/15 flex items-center justify-center shrink-0">
                  <Zap className="w-3.5 h-3.5 text-accent" />
                </div>
                <div>
                  <p className="text-text-primary text-sm font-bold leading-tight">Venta rápida</p>
                  <p className="text-text-muted text-[10px]">Busca un miembro o crea uno nuevo</p>
                </div>
              </div>
              <button
                onClick={() => navigate('/members/new')}
                className="flex items-center gap-1.5 text-[11px] text-accent hover:opacity-80 transition-opacity cursor-pointer font-semibold bg-accent/8 border border-accent/15 px-3 py-1.5 rounded-xl"
              >
                <UserPlus className="w-3 h-3" /> Nuevo
              </button>
            </div>

            <div className="px-4 py-4 flex flex-col gap-3">
              <MemberSearch
                onSelect={setSelectedUser}
                onClear={() => setSelectedUser(null)}
                value={selectedUser}
              />

              <button
                onClick={() => setMembershipOpen(true)}
                className={`w-full group flex items-center gap-3 px-4 py-3.5 rounded-xl transition-all cursor-pointer ${
                  selectedUser
                    ? 'bg-accent text-background hover:opacity-90 shadow-md shadow-accent/20'
                    : 'bg-accent/8 border border-accent/20 text-accent hover:bg-accent/15'
                }`}
              >
                <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${selectedUser ? 'bg-background/20' : 'bg-accent/15'}`}>
                  <Banknote className="w-5 h-5" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-bold text-sm leading-tight">
                    {selectedUser ? `Cobrar a ${selectedUser.name.split(' ')[0]}` : 'Cobrar membresía'}
                  </p>
                  <p className={`text-[11px] mt-0.5 ${selectedUser ? 'opacity-70' : 'text-accent/60'}`}>
                    Asignar plan y registrar pago
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 shrink-0 opacity-50 group-hover:translate-x-0.5 transition-transform" />
              </button>

              <button
                onClick={() => navigate('/payments/new')}
                className="w-full group flex items-center gap-3 px-4 py-3.5 rounded-xl bg-blue-600 hover:bg-blue-500 text-white transition-colors cursor-pointer"
              >
                <div className="w-9 h-9 rounded-xl bg-white/15 flex items-center justify-center shrink-0">
                  <CreditCard className="w-5 h-5" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-bold text-sm leading-tight">Registrar cobro</p>
                  <p className="text-white/65 text-[11px] mt-0.5">Pago directo o suelto</p>
                </div>
                <ChevronRight className="w-4 h-4 shrink-0 opacity-50 group-hover:translate-x-0.5 transition-transform" />
              </button>

              <button
                onClick={() => navigate('/payments')}
                className="flex items-center justify-center gap-1.5 py-1 text-[11px] text-text-muted hover:text-text-primary transition-colors cursor-pointer"
              >
                Ver historial de cobros <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>

          {/* Abrir puerta */}
          <button
            onClick={() => { if (isElectron && proxyUrl) setDoorOpen(true) }}
            disabled={!isElectron || !proxyUrl}
            className="group flex items-center gap-3 px-5 py-4 rounded-2xl bg-surface border border-border hover:border-amber-500/40 hover:bg-amber-500/5 transition-all cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed -translate-y-0 hover:-translate-y-0.5 hover:shadow-md"
          >
            <div className="w-10 h-10 rounded-xl bg-amber-500/10 border border-amber-500/20 flex items-center justify-center shrink-0">
              <DoorOpen className="w-5 h-5 text-amber-500" />
            </div>
            <div className="flex-1 text-left">
              <p className="text-text-primary font-bold text-sm">Abrir puerta</p>
              <p className="text-text-muted text-[11px] mt-0.5">{isElectron ? 'Control de acceso manual' : 'Requiere app de escritorio'}</p>
            </div>
            <ChevronRight className="w-4 h-4 text-text-muted group-hover:text-amber-500 group-hover:translate-x-0.5 transition-all shrink-0" />
          </button>

          {/* Gráficas — en columna izquierda para no desperdiciar espacio */}
          <div className="bg-surface border border-border rounded-2xl px-5 pt-4 pb-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-accent/10 border border-accent/15 flex items-center justify-center">
                  <TrendingUp className="w-3.5 h-3.5 text-accent" />
                </div>
                <p className="text-xs font-bold text-text-primary">Esta semana</p>
              </div>
              <span className="text-[10px] text-text-muted tabular-nums font-semibold">
                {weekDays.reduce((s, d) => s + d.count, 0)} accesos en 7 días
              </span>
            </div>
            {weekDays.length > 0
              ? <AreaChart days={weekDays} />
              : <div className="h-16 flex items-center justify-center"><p className="text-text-muted text-xs">Cargando...</p></div>
            }
          </div>

          <div className="bg-surface border border-border rounded-2xl px-5 pt-4 pb-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <div className="w-7 h-7 rounded-lg bg-blue-500/10 border border-blue-500/15 flex items-center justify-center">
                  <Clock className="w-3.5 h-3.5 text-blue-400" />
                </div>
                <p className="text-xs font-bold text-text-primary">Hoy por hora</p>
              </div>
              <span className="text-[10px] text-text-muted font-semibold">
                Pico: {peakHour > 0 && Math.max(...hourlyData) > 0 ? `${peakHour}:00` : '—'}
              </span>
            </div>
            <HourBars hourly={hourlyData} />
          </div>

        </div>

        {/* ── Columna derecha: Feed en vivo ── */}
        <div className="bg-surface border border-border rounded-2xl overflow-hidden flex flex-col min-h-[500px]">

          {/* Header del feed */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-accent/10 border border-accent/15 flex items-center justify-center shrink-0">
                <Activity className="w-4 h-4 text-accent" />
              </div>
              <div>
                <p className="text-text-primary text-sm font-bold leading-tight">Actividad de hoy</p>
                <p className="text-text-muted text-[10px] mt-0.5">
                  {metrics.todayAccesses > 0 ? `${metrics.todayAccesses} eventos registrados` : 'Sin eventos aún'}
                </p>
              </div>
            </div>
            <span className={`flex items-center gap-1.5 text-[10px] font-bold px-2.5 py-1 rounded-full border ${isElectron ? 'text-emerald-600 dark:text-emerald-400 bg-emerald-500/8 border-emerald-500/20' : 'text-text-muted bg-surface border-border'}`}>
              <span className={`w-1.5 h-1.5 rounded-full ${isElectron ? 'bg-emerald-500 animate-pulse' : 'bg-border'}`} />
              {isElectron ? 'En vivo' : 'Cada 30 s'}
            </span>
          </div>

          {/* Lista de eventos — solo registros con nombre identificado; los
              intentos sin coincidencia (tarjeta/rostro no reconocido, ruido
              del propio dispositivo) no aportan nada útil en este resumen. */}
          {namedFeedEvents.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center gap-3 py-12">
              <div className="w-12 h-12 rounded-2xl bg-background border border-border flex items-center justify-center">
                <Zap className="w-5 h-5 text-text-muted opacity-40" />
              </div>
              <div className="text-center">
                <p className="text-text-primary text-sm font-semibold">Sin actividad hoy</p>
                <p className="text-text-muted text-xs mt-1">Los accesos al gimnasio aparecerán aquí en tiempo real</p>
              </div>
            </div>
          ) : (
            <div className="overflow-y-auto flex-1 divide-y divide-border">
              {namedFeedEvents.map((ev, i) => {
                const granted = ev.event_type === 'ACCESS_GRANTED'
                const evExt = ev as AccessEvent & { user_id?: string }
                const name = ev.user_name!
                // ev.user_id only exists on rows loaded from the initial DB
                // fetch — live SSE rows carry employee_no only, so resolve
                // through that for both sources uniformly.
                const resolvedUser = ev.employee_no ? userByEmployeeNo.get(ev.employee_no) : undefined
                const resolvedUserId = evExt.user_id ?? resolvedUser?.id
                const membership = resolvedUserId ? membershipByUser.get(resolvedUserId) : undefined
                // Staff/partner/long-term members don't need a membership to
                // get in (see device-stream-manager's access-authorization
                // check) — showing "Sin membresía activa" for them here would
                // just be misleading noise, not a real problem to flag.
                const isExempt = resolvedUser?.member_category === 'staff' || resolvedUser?.member_category === 'partner' || resolvedUser?.long_term
                const status = resolvedUserId && !isExempt ? membershipMessage(membership?.end_date) : undefined
                return (
                  <div
                    key={`${ev.id}-${i}`}
                    className={`flex items-center gap-3.5 px-5 py-3.5 transition-colors hover:bg-background/50 ${!granted ? 'bg-red-500/3' : ''}`}
                  >
                    {/* Avatar */}
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 text-[11px] font-black ${granted ? 'bg-accent/12 text-accent' : 'bg-red-500/12 text-red-500'}`}>
                      {initials(name)}
                    </div>

                    {/* Info */}
                    <div className="flex-1 min-w-0">
                      <p className="text-text-primary text-sm font-semibold leading-tight truncate">{name}</p>
                      <div className={`flex items-center gap-1 mt-0.5 ${granted ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-500'}`}>
                        {granted
                          ? <><Check className="w-3 h-3" /><span className="text-[10px] font-semibold">Acceso concedido</span></>
                          : <><X className="w-3 h-3" /><span className="text-[10px] font-semibold">Acceso denegado</span></>}
                      </div>
                      {status && (
                        <p className={`text-[10px] mt-0.5 ${
                          status.tone === 'expired' ? 'text-red-500' : status.tone === 'warn' ? 'text-amber-500' : 'text-text-muted'
                        }`}>
                          {status.message}
                        </p>
                      )}
                    </div>

                    {/* Tiempo + acción */}
                    <div className="flex flex-col items-end gap-1.5 shrink-0">
                      <span className="text-[10px] text-text-muted tabular-nums font-medium">{timeAgo(ev.occurred_at)}</span>
                      {resolvedUserId && (
                        <button
                          onClick={() => navigate(`/members/${resolvedUserId}`)}
                          className="text-[10px] text-accent hover:underline cursor-pointer font-semibold"
                        >
                          Ver perfil
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Modales ── */}
      <MembershipForm
        open={membershipOpen}
        mode="create"
        prefillUser={selectedUser ?? undefined}
        onClose={() => setMembershipOpen(false)}
        onSaved={() => { setMembershipOpen(false); setSelectedUser(null) }}
      />

      <Dialog open={doorOpen} onOpenChange={setDoorOpen}>
        <DialogContent className="bg-surface border-border text-text-primary max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <DoorOpen className="w-4 h-4 text-amber-400" /> Control de puertas
            </DialogTitle>
          </DialogHeader>
          {proxyUrl && proxyToken && (
            <DoorPanel devices={devices.map(d => ({ id: d.id, name: d.name, status: d.status }))} proxyUrl={proxyUrl} proxyToken={proxyToken} />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}

// ── Tarjeta de métrica ────────────────────────────────────────────────────────
const COLOR_MAP = {
  emerald: {
    icon:  'bg-emerald-500/10 border-emerald-500/20 text-emerald-500',
    value: 'text-emerald-600 dark:text-emerald-400',
    card:  'border-emerald-500/20 hover:border-emerald-500/35',
  },
  blue: {
    icon:  'bg-blue-500/10 border-blue-500/20 text-blue-500',
    value: 'text-blue-600 dark:text-blue-400',
    card:  'border-blue-500/20 hover:border-blue-500/35',
  },
  red: {
    icon:  'bg-red-500/10 border-red-500/20 text-red-500',
    value: 'text-red-600 dark:text-red-400',
    card:  'border-red-500/25 hover:border-red-500/40',
  },
  amber: {
    icon:  'bg-amber-500/10 border-amber-500/20 text-amber-500',
    value: 'text-amber-600 dark:text-amber-400',
    card:  'border-amber-500/25 hover:border-amber-500/40',
  },
  muted: {
    icon:  'bg-background border-border text-text-muted',
    value: 'text-text-primary',
    card:  'border-border hover:border-border',
  },
} as const

function MetricCard({ icon, label, value, detail, color, action }: {
  icon: React.ReactNode
  label: string
  value: number
  detail: string
  color: keyof typeof COLOR_MAP
  action?: { label: string; onClick: () => void }
}) {
  const c = COLOR_MAP[color]
  return (
    <div className={`bg-surface border rounded-2xl px-4 py-4 flex flex-col gap-3 transition-all hover:-translate-y-0.5 hover:shadow-lg ${c.card}`}>
      <div className="flex items-start justify-between">
        <div className={`w-9 h-9 rounded-xl border flex items-center justify-center shrink-0 ${c.icon}`}>
          {icon}
        </div>
        {action && (
          <button
            onClick={action.onClick}
            className="text-[10px] text-text-muted hover:text-text-primary font-semibold transition-colors cursor-pointer"
          >
            {action.label} →
          </button>
        )}
      </div>
      <div>
        <p className={`text-3xl font-black tabular-nums leading-none ${c.value}`}>{value}</p>
        <p className="text-text-muted text-[10px] font-semibold mt-1 uppercase tracking-wide">{label}</p>
        <p className="text-text-muted text-[10px] mt-0.5 opacity-70">{detail}</p>
      </div>
    </div>
  )
}
